// GENERATED FILE — DO NOT EDIT.
// Source of truth: config/subprocessors.json
// Regenerate: node scripts/generate-subprocessors.mjs
//
// This list is PUBLISHED at /trust. It is a representation about who receives
// customer data, so it is generated from the SSOT the sync gate checks against
// the running code, never typed into the page by hand.

export type SubprocessorRole =
  | "infrastructure"
  | "model-gateway"
  | "model-provider"
  | "media-provider"
  | "connections"
  | "payments"
  | "communications"
  | "operations";

/** How a vendor is reached: straight from our servers, through the model gateway, or both. */
export type SubprocessorReach = 'direct' | 'via-openrouter' | 'both';

export interface Subprocessor {
  id: string;
  name: string;
  role: SubprocessorRole;
  purpose: string;
  dataCategories: string[];
  region: string;
  reach: SubprocessorReach;
  /** Whether customer content or account data reaches this vendor at all. */
  customerData: boolean;
  url: string;
}

/** The date this list was last reviewed against the running system. */
export const SUBPROCESSORS_LAST_REVIEWED = "2026-09-11";

export const SUBPROCESSORS: readonly Subprocessor[] = [
  {
    id: "google-cloud",
    name: "Google Cloud Platform (incl. Firebase)",
    role: "infrastructure",
    purpose: "Hosting, database (Firestore), file storage, authentication, logging, and the workstation fleet. The platform runs here.",
    dataCategories: ["account", "content", "files", "usage", "logs"],
    region: "United States (us-central1)",
    reach: "direct",
    customerData: true,
    url: "https://cloud.google.com/terms/subprocessors",
  },
  {
    id: "openrouter",
    name: "OpenRouter",
    role: "model-gateway",
    purpose: "Routes every text model request to the provider the tier registry names. Prompts and the conversation context transit OpenRouter.",
    dataCategories: ["content"],
    region: "United States",
    reach: "direct",
    customerData: true,
    url: "https://openrouter.ai/privacy",
  },
  {
    id: "alibaba",
    name: "Alibaba Cloud (Qwen / DashScope)",
    role: "model-provider",
    purpose: "Qwen embedding and language models, and Wan video generation.",
    dataCategories: ["content"],
    region: "Singapore (international endpoint)",
    reach: "both",
    customerData: true,
    url: "https://www.alibabacloud.com/help/en/legal/latest/alibaba-cloud-international-website-privacy-policy",
  },
  {
    id: "amazon",
    name: "Amazon Web Services (Bedrock / Nova)",
    role: "model-provider",
    purpose: "Nova models.",
    dataCategories: ["content"],
    region: "United States",
    reach: "both",
    customerData: true,
    url: "https://aws.amazon.com/privacy/",
  },
  {
    id: "anthropic",
    name: "Anthropic",
    role: "model-provider",
    purpose: "Claude models on several tiers.",
    dataCategories: ["content"],
    region: "United States",
    reach: "via-openrouter",
    customerData: true,
    url: "https://www.anthropic.com/legal/privacy",
  },
  {
    id: "google-ai",
    name: "Google (Gemini / Vertex AI)",
    role: "model-provider",
    purpose: "Gemini models on several tiers, and Veo video generation.",
    dataCategories: ["content"],
    region: "United States",
    reach: "both",
    customerData: true,
    url: "https://policies.google.com/privacy",
  },
  {
    id: "groq",
    name: "Groq",
    role: "model-provider",
    purpose: "Low-latency inference for speech models.",
    dataCategories: ["content"],
    region: "United States",
    reach: "via-openrouter",
    customerData: true,
    url: "https://groq.com/privacy-policy/",
  },
  {
    id: "openai",
    name: "OpenAI",
    role: "model-provider",
    purpose: "GPT models on several tiers — image generation and the realtime voice session, which are reached directly rather than through the gateway.",
    dataCategories: ["content"],
    region: "United States",
    reach: "both",
    customerData: true,
    url: "https://openai.com/policies/privacy-policy",
  },
  {
    id: "perplexity",
    name: "Perplexity",
    role: "model-provider",
    purpose: "Web search and deep research. The search query reaches Perplexity — the workspace's own documents do not.",
    dataCategories: ["content"],
    region: "United States",
    reach: "direct",
    customerData: true,
    url: "https://www.perplexity.ai/hub/legal/privacy-policy",
  },
  {
    id: "xai",
    name: "xAI",
    role: "model-provider",
    purpose: "Grok models on several tiers.",
    dataCategories: ["content"],
    region: "United States",
    reach: "via-openrouter",
    customerData: true,
    url: "https://x.ai/legal/privacy-policy",
  },
  {
    id: "black-forest-labs",
    name: "Black Forest Labs (FLUX)",
    role: "media-provider",
    purpose: "Image generation.",
    dataCategories: ["content"],
    region: "Germany / United States",
    reach: "direct",
    customerData: true,
    url: "https://blackforestlabs.ai/privacy-policy/",
  },
  {
    id: "bytedance",
    name: "ByteDance (BytePlus / Seedance)",
    role: "media-provider",
    purpose: "Video generation.",
    dataCategories: ["content"],
    region: "Singapore (BytePlus international endpoint)",
    reach: "both",
    customerData: true,
    url: "https://www.byteplus.com/en/legal/privacy-policy",
  },
  {
    id: "elevenlabs",
    name: "ElevenLabs",
    role: "media-provider",
    purpose: "Speech synthesis (including reading a message aloud), sound effects, and the two-way voice session.",
    dataCategories: ["content", "voice"],
    region: "United States",
    reach: "direct",
    customerData: true,
    url: "https://elevenlabs.io/privacy-policy",
  },
  {
    id: "fal",
    name: "fal.ai",
    role: "media-provider",
    purpose: "Hosted inference for media models, including adding synchronized sound to a clip.",
    dataCategories: ["content", "files"],
    region: "United States",
    reach: "direct",
    customerData: true,
    url: "https://fal.ai/privacy",
  },
  {
    id: "minimax",
    name: "MiniMax",
    role: "media-provider",
    purpose: "Video generation.",
    dataCategories: ["content"],
    region: "Singapore (international endpoint)",
    reach: "both",
    customerData: true,
    url: "https://www.minimax.io/privacy-policy",
  },
  {
    id: "composio",
    name: "Composio",
    role: "connections",
    purpose: "Brokers the authorized connections to the applications a workspace links (mail, calendar, CRM and similar). Holds the connection tokens and passes through the data each connected application returns.",
    dataCategories: ["account", "connected-application-data", "credentials"],
    region: "United States",
    reach: "direct",
    customerData: true,
    url: "https://composio.dev/privacy-policy",
  },
  {
    id: "stripe",
    name: "Stripe",
    role: "payments",
    purpose: "Subscriptions, invoices and payment methods. Card details go to Stripe directly and never reach Prest0's servers.",
    dataCategories: ["account", "billing"],
    region: "United States",
    reach: "direct",
    customerData: true,
    url: "https://stripe.com/privacy",
  },
  {
    id: "email-delivery",
    name: "Resend and SendGrid (Twilio)",
    role: "communications",
    purpose: "Transactional email — sign-in links, notifications, billing notices.",
    dataCategories: ["account", "email", "message-content"],
    region: "United States",
    reach: "direct",
    customerData: true,
    url: "https://resend.com/legal/privacy-policy",
  },
  {
    id: "twilio",
    name: "Twilio",
    role: "communications",
    purpose: "Text messages, where a workspace uses them.",
    dataCategories: ["account", "phone", "message-content"],
    region: "United States",
    reach: "direct",
    customerData: true,
    url: "https://www.twilio.com/legal/privacy",
  },
  {
    id: "2captcha",
    name: "2Captcha",
    role: "operations",
    purpose: "Solves an interactive challenge when a workstation session meets one while carrying out a task the workspace asked for. It receives the challenge, not the workspace's data.",
    dataCategories: ["challenge-image"],
    region: "Outside the United States",
    reach: "direct",
    customerData: false,
    url: "https://2captcha.com/privacy-policy",
  },
  {
    id: "github",
    name: "GitHub",
    role: "operations",
    purpose: "Source control and the engineering procedures repository. No customer content is sent here.",
    dataCategories: [],
    region: "United States",
    reach: "direct",
    customerData: false,
    url: "https://docs.github.com/en/site-policy/privacy-policies/github-general-privacy-statement",
  },
  {
    id: "slack",
    name: "Slack (Salesforce)",
    role: "operations",
    purpose: "Prest0's own internal alerting. No customer content is sent here.",
    dataCategories: [],
    region: "United States",
    reach: "direct",
    customerData: false,
    url: "https://slack.com/trust/privacy/privacy-policy",
  },
];

/**
 * One or two plain lines per subprocessor, for the public-site index the
 * signed-out Prest0n searches (D13). Not rendered — the page renders the
 * structured list above. See the generator for why this shape exists.
 */
export const SUBPROCESSOR_LINES: readonly string[] = [
  "Google Cloud Platform (incl. Firebase) — where the platform runs. Data received: account, content, files, usage, logs. Region: United States (us-central1). Called directly by prest0.",
  "Google Cloud Platform (incl. Firebase): Hosting, database (Firestore), file storage, authentication, logging, and the workstation fleet. The platform runs here.",
  "OpenRouter — the model gateway. Data received: content. Region: United States. Called directly by prest0.",
  "OpenRouter: Routes every text model request to the provider the tier registry names. Prompts and the conversation context transit OpenRouter.",
  "Alibaba Cloud (Qwen / DashScope) — a model provider. Data received: content. Region: Singapore (international endpoint). Reached both directly and through the gateway.",
  "Alibaba Cloud (Qwen / DashScope): Qwen embedding and language models, and Wan video generation.",
  "Amazon Web Services (Bedrock / Nova) — a model provider. Data received: content. Region: United States. Reached both directly and through the gateway.",
  "Amazon Web Services (Bedrock / Nova): Nova models.",
  "Anthropic — a model provider. Data received: content. Region: United States. Reached through the model gateway.",
  "Anthropic: Claude models on several tiers.",
  "Google (Gemini / Vertex AI) — a model provider. Data received: content. Region: United States. Reached both directly and through the gateway.",
  "Google (Gemini / Vertex AI): Gemini models on several tiers, and Veo video generation.",
  "Groq — a model provider. Data received: content. Region: United States. Reached through the model gateway.",
  "Groq: Low-latency inference for speech models.",
  "OpenAI — a model provider. Data received: content. Region: United States. Reached both directly and through the gateway.",
  "OpenAI: GPT models on several tiers — image generation and the realtime voice session, which are reached directly rather than through the gateway.",
  "Perplexity — a model provider. Data received: content. Region: United States. Called directly by prest0.",
  "Perplexity: Web search and deep research. The search query reaches Perplexity — the workspace's own documents do not.",
  "xAI — a model provider. Data received: content. Region: United States. Reached through the model gateway.",
  "xAI: Grok models on several tiers.",
  "Black Forest Labs (FLUX) — pictures, video and voice. Data received: content. Region: Germany / United States. Called directly by prest0.",
  "Black Forest Labs (FLUX): Image generation.",
  "ByteDance (BytePlus / Seedance) — pictures, video and voice. Data received: content. Region: Singapore (BytePlus international endpoint). Reached both directly and through the gateway.",
  "ByteDance (BytePlus / Seedance): Video generation.",
  "ElevenLabs — pictures, video and voice. Data received: content, voice. Region: United States. Called directly by prest0.",
  "ElevenLabs: Speech synthesis (including reading a message aloud), sound effects, and the two-way voice session.",
  "fal.ai — pictures, video and voice. Data received: content, files. Region: United States. Called directly by prest0.",
  "fal.ai: Hosted inference for media models, including adding synchronized sound to a clip.",
  "MiniMax — pictures, video and voice. Data received: content. Region: Singapore (international endpoint). Reached both directly and through the gateway.",
  "MiniMax: Video generation.",
  "Composio — the broker for applications you connect. Data received: account, connected-application-data, credentials. Region: United States. Called directly by prest0.",
  "Composio: Brokers the authorized connections to the applications a workspace links (mail, calendar, CRM and similar). Holds the connection tokens and passes through the data each connected application returns.",
  "Stripe — payments. Data received: account, billing. Region: United States. Called directly by prest0.",
  "Stripe: Subscriptions, invoices and payment methods. Card details go to Stripe directly and never reach Prest0's servers.",
  "Resend and SendGrid (Twilio) — email and text messages. Data received: account, email, message-content. Region: United States. Called directly by prest0.",
  "Resend and SendGrid (Twilio): Transactional email — sign-in links, notifications, billing notices.",
  "Twilio — email and text messages. Data received: account, phone, message-content. Region: United States. Called directly by prest0.",
  "Twilio: Text messages, where a workspace uses them.",
  "2Captcha — running the service. Data received: challenge-image. Region: Outside the United States. Called directly by prest0.",
  "2Captcha: Solves an interactive challenge when a workstation session meets one while carrying out a task the workspace asked for. It receives the challenge, not the workspace's data.",
  "GitHub — running the service. Data received: no customer data. Region: United States. Called directly by prest0.",
  "GitHub: Source control and the engineering procedures repository. No customer content is sent here.",
  "Slack (Salesforce) — running the service. Data received: no customer data. Region: United States. Called directly by prest0.",
  "Slack (Salesforce): Prest0's own internal alerting. No customer content is sent here.",
];

/** Plain-language headings for each group, in the order the page shows them. */
export const ROLE_LABELS: Record<SubprocessorRole, string> = {
  'infrastructure': 'where the platform runs',
  'model-gateway': 'how model requests are routed',
  'model-provider': 'the models that answer',
  'media-provider': 'pictures, video and voice',
  'connections': 'the applications you connect',
  'payments': 'payments',
  'communications': 'email and text messages',
  'operations': 'running the service',
};
